import { createContext, useCallback, useContext, useEffect, useState, type ReactNode } from 'react';

/**
 * Minimal typed i18n with RTL. UI chrome is translated; business content (product names, etc.) uses
 * the EN/AR fields stored on each record. Add a locale = add a dictionary.
 */
const en = {
  'nav.dashboard': 'Dashboard',
  'nav.catalog': 'Catalogue',
  'nav.suppliers': 'Suppliers',
  'nav.rfqs': 'RFQs',
  'nav.orders': 'Orders',
  'nav.approvals': 'Approvals',
  'nav.invoices': 'Invoices',
  'nav.disputes': 'Disputes',
  'nav.cart': 'Cart',
  'nav.products': 'Products',
  'nav.finance': 'Payouts',
  'nav.company': 'Company',
  'nav.support': 'Help & support',
  'nav.settings': 'My settings',
  'nav.organizations': 'Organisations',
  'nav.kyc': 'Onboarding review',
  'nav.moderation': 'Moderation',
  'nav.categories': 'Categories',
  'nav.users': 'Users',
  'nav.payments': 'Payments',
  'nav.settlements': 'Settlements',
  'nav.commissions': 'Commissions',
  'nav.content': 'Announcements',
  'nav.audit': 'Audit trail',
  'nav.configuration': 'Configuration',
  'nav.integrations': 'Integrations',
  'nav.onboarding': 'Onboarding',
  'common.signOut': 'Sign out',
  'common.search': 'Search',
  'common.save': 'Save changes',
  'common.cancel': 'Cancel',
  'common.notifications': 'Notifications',
  'common.markAllRead': 'Mark all as read',
  'common.loading': 'Loading…',
  'common.language': 'العربية',
  'section.buy': 'Buy',
  'section.sell': 'Sell',
  'section.operate': 'Operate',
  'section.money': 'Money',
  'section.platform': 'Platform',
  'section.account': 'Account',
} as const;

type Key = keyof typeof en;

const ar: Record<Key, string> = {
  'nav.dashboard': 'لوحة التحكم',
  'nav.catalog': 'الكتالوج',
  'nav.suppliers': 'الموردون',
  'nav.rfqs': 'طلبات عروض الأسعار',
  'nav.orders': 'الطلبات',
  'nav.approvals': 'الاعتمادات',
  'nav.invoices': 'الفواتير',
  'nav.disputes': 'النزاعات',
  'nav.cart': 'السلة',
  'nav.products': 'المنتجات',
  'nav.finance': 'المدفوعات للمورد',
  'nav.company': 'الشركة',
  'nav.support': 'المساعدة والدعم',
  'nav.settings': 'إعداداتي',
  'nav.organizations': 'المؤسسات',
  'nav.kyc': 'مراجعة التسجيل',
  'nav.moderation': 'مراجعة المنتجات',
  'nav.categories': 'الفئات',
  'nav.users': 'المستخدمون',
  'nav.payments': 'المدفوعات',
  'nav.settlements': 'التسويات',
  'nav.commissions': 'العمولات',
  'nav.content': 'الإعلانات',
  'nav.audit': 'سجل التدقيق',
  'nav.configuration': 'الإعدادات العامة',
  'nav.integrations': 'التكاملات',
  'nav.onboarding': 'التسجيل',
  'common.signOut': 'تسجيل الخروج',
  'common.search': 'بحث',
  'common.save': 'حفظ التغييرات',
  'common.cancel': 'إلغاء',
  'common.notifications': 'الإشعارات',
  'common.markAllRead': 'تعليم الكل كمقروء',
  'common.loading': 'جارٍ التحميل…',
  'common.language': 'English',
  'section.buy': 'الشراء',
  'section.sell': 'البيع',
  'section.operate': 'التشغيل',
  'section.money': 'المالية',
  'section.platform': 'المنصة',
  'section.account': 'الحساب',
};

const dictionaries = { en, ar } as const;
export type Locale = keyof typeof dictionaries;

interface I18n {
  locale: Locale;
  dir: 'ltr' | 'rtl';
  t: (key: Key) => string;
  setLocale: (l: Locale) => void;
}
const Ctx = createContext<I18n>(null!);

export function I18nProvider({ children }: { children: ReactNode }) {
  const [locale, setLocaleState] = useState<Locale>(() => {
    try {
      return (localStorage.getItem('hc.locale') as Locale) || 'en';
    } catch {
      return 'en';
    }
  });
  const dir = locale === 'ar' ? 'rtl' : 'ltr';
  useEffect(() => {
    document.documentElement.lang = locale;
    document.documentElement.dir = dir;
  }, [locale, dir]);
  const setLocale = useCallback((l: Locale) => {
    setLocaleState(l);
    try {
      localStorage.setItem('hc.locale', l);
    } catch {
      /* private mode */
    }
  }, []);
  const t = useCallback((key: Key) => dictionaries[locale][key] ?? en[key] ?? key, [locale]);
  return <Ctx.Provider value={{ locale, dir, t, setLocale }}>{children}</Ctx.Provider>;
}

export const useI18n = () => useContext(Ctx);
